import { Injectable, computed, signal } from '@angular/core';

export const UPDATE_BLOCKER_REASONS = [
  'checkin',
  'scan',
  'camera',
  'local-save',
  'checkout-sync',
  'draft-remito',
  'draft-photo'
] as const;

export type UpdateBlockerReason = (typeof UPDATE_BLOCKER_REASONS)[number];

type BlockerOwners = Record<UpdateBlockerReason, string[]>;

const createEmptyBlockers = (): BlockerOwners => ({
  checkin: [],
  scan: [],
  camera: [],
  'local-save': [],
  'checkout-sync': [],
  'draft-remito': [],
  'draft-photo': []
});

@Injectable({ providedIn: 'root' })
export class BusyStateService {
  private readonly blockerOwners = signal<BlockerOwners>(createEmptyBlockers());

  readonly blockingReasons = computed<UpdateBlockerReason[]>(() => {
    const blockers = this.blockerOwners();
    return UPDATE_BLOCKER_REASONS.filter(reason => blockers[reason].length > 0);
  });

  readonly hasOpenCheckinSession = computed(() => this.hasBlockingReason('checkin'));
  readonly isScanningQr = computed(() => this.hasBlockingReason('scan'));
  readonly isCapturingPhoto = computed(() => this.hasBlockingReason('camera'));
  readonly isSavingLocal = computed(() => this.hasBlockingReason('local-save'));
  readonly isSending = computed(() => this.hasBlockingReason('checkout-sync'));
  readonly hasDraftRemito = computed(() => this.hasBlockingReason('draft-remito'));
  readonly hasDraftPhoto = computed(() => this.hasBlockingReason('draft-photo'));

  readonly isSafeToReload = computed(() => this.blockingReasons().length === 0);

  setCheckInSessionActive(active: boolean): void {
    this.setBlocker('checkin', 'checkin-session', active);
  }

  setScanningQrActive(active: boolean): void {
    this.setBlocker('scan', 'scan-qr', active);
  }

  setCapturingPhotoActive(active: boolean): void {
    this.setBlocker('camera', 'capture-photo', active);
  }

  setLocalSaveActive(owner: string, active: boolean): void {
    this.setBlocker('local-save', owner, active);
  }

  setSendingActive(owner: string, active: boolean): void {
    this.setBlocker('checkout-sync', owner, active);
  }

  setDraftRemitoActive(active: boolean): void {
    this.setBlocker('draft-remito', 'remito-state', active);
  }

  setDraftPhotoActive(active: boolean): void {
    this.setBlocker('draft-photo', 'remito-photo', active);
  }

  hasBlockingReason(reason: UpdateBlockerReason): boolean {
    return this.blockerOwners()[reason].length > 0;
  }

  private setBlocker(reason: UpdateBlockerReason, owner: string, active: boolean): void {
    this.blockerOwners.update(current => {
      const existingOwners = current[reason];
      const alreadyRegistered = existingOwners.includes(owner);

      if (active && alreadyRegistered) {
        return current;
      }

      if (!active && !alreadyRegistered) {
        return current;
      }

      return {
        ...current,
        [reason]: active
          ? [...existingOwners, owner]
          : existingOwners.filter(existingOwner => existingOwner !== owner)
      };
    });
  }
}
